import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { Badge, type BadgeTone } from '@/shared/ui/badge';

/**
 * LabelBadge — pilulka „popisek + stavový badge". Levá část nese popisek
 * (název entity/dokumentu), pravá vnořený solid {@link Badge} se stavem.
 * Kontejner i vnořený badge sdílejí jeden tón.
 *
 * Kontejner per tón: tintované pozadí, tintovaná linka (sémantická barva se
 * sníženou průhledností) a popisek ve stejném odstínu. V dark modu se text
 * (stejně jako u soft Badge) přepne na mode-aware sémantickou bázi (--phx-*).
 */
const CONTAINER_CLASSES: Record<BadgeTone, string> = {
  neutral: 'bg-phx-surface-2 text-phx-text-2 border-phx-border-3',
  primary:
    'bg-phx-primary-tint text-phx-primary-active dark:text-phx-primary border-phx-primary/35',
  success: 'bg-phx-success-tint text-phx-success-text dark:text-phx-success border-phx-success/35',
  warning: 'bg-phx-warning-tint text-phx-warning-text dark:text-phx-warning border-phx-warning/40',
  danger: 'bg-phx-danger-tint text-phx-danger-text dark:text-phx-danger border-phx-danger/35',
  info: 'bg-phx-info-tint text-phx-info-text dark:text-phx-info border-phx-info/35',
  draft: 'bg-phx-draft-tint text-phx-draft-text dark:text-phx-draft border-phx-draft/40',
};

const DISABLED_CONTAINER =
  'bg-phx-surface-2 text-phx-text-muted border-phx-border-2 opacity-60 pointer-events-none';

const BASE_LABEL_BADGE =
  'phx-label-badge inline-flex items-center gap-phx-4 h-phx-8 pl-phx-4_5 pr-phx-1 ' +
  'border rounded-full whitespace-nowrap text-phx-sm font-medium leading-none';

export interface LabelBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'style'> {
  /** Tón určuje barvy kontejneru i vnořeného stavového badge. */
  tone?: BadgeTone;
  /** Levý popisek (např. „General & Terms"). */
  label: ReactNode;
  /** Stav vpravém badge (např. „APPROVED"). */
  status: ReactNode;
  disabled?: boolean;
}

export const LabelBadge = forwardRef<HTMLSpanElement, LabelBadgeProps>(function LabelBadge(
  { tone = 'neutral', label, status, disabled = false, className, ...rest },
  ref
) {
  return (
    <span
      ref={ref}
      data-tone={tone}
      data-disabled={disabled || undefined}
      className={cn(
        BASE_LABEL_BADGE,
        disabled ? DISABLED_CONTAINER : CONTAINER_CLASSES[tone],
        className
      )}
      {...rest}
    >
      {label}
      <Badge
        tone={tone}
        appearance="solid"
        disabled={disabled}
        // Pilulkový tvar a vyšší výška vnořeného badge; opacity-100 ruší
        // dvojí ztlumení (kontejner už nese opacity-60 v disabled stavu).
        className={cn('h-phx-control-xs rounded-full', disabled && 'opacity-100')}
      >
        {status}
      </Badge>
    </span>
  );
});
